import type { Dispatch } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import type { FocusOverride, QueryBuilderAction } from '../../../types/QueryBuilder';

import type { GridSelectionState } from './useGridSelection';

type UndoItem = {
  query: string;
  focusedKey: string | number | null;
  focusOverride: FocusOverride | null | undefined;
};

const MAX_ITEMS = 100;

/**
 * Pushes a new entry when the query changes, or updates the top entry's focusedKey
 * in-place when only focus moves (no push). Returns true when a new entry was
 * pushed. Idempotent — re-running with the same arguments is a no-op, so it's safe
 * to invoke from an effect that may re-fire on StrictMode/concurrent retries.
 */
function updateUndoStack({
  undoStack,
  query,
  focusOverride,
  focusedKey,
}: {
  undoStack: UndoItem[];
  query: string;
  focusOverride: FocusOverride | null;
  focusedKey: string | number | null;
}): boolean {
  const top = undoStack[undoStack.length - 1];

  if (!top || top.query !== query) {
    undoStack.push({ query, focusedKey, focusOverride });
    if (undoStack.length > MAX_ITEMS) {
      undoStack.splice(0, undoStack.length - MAX_ITEMS);
    }
    return true;
  }
  if (top.focusedKey !== focusedKey) {
    // Only focus moved — update in place so the restored focus position is always
    // the last place focus was while this query was current, not the first.
    top.focusedKey = focusedKey;
  }
  return false;
}

/** Derive the focusOverride to restore alongside a query snapshot. */
function focusOverrideForItem(item: UndoItem): FocusOverride | undefined {
  return item.focusOverride ?? (item.focusedKey !== null ? { itemKey: item.focusedKey.toString() } : undefined);
}

/**
 * Undo history for the query builder.
 *
 * Snapshots are written from a `useEffect` after commit so React's purity rules
 * are upheld; the helper is idempotent so a StrictMode double-invoke (or any
 * concurrent retry) doesn't push duplicate entries. Each unique query string
 * gets its own entry; focus position within a query is updated in-place as the
 * user moves between tokens.
 *
 * Call `undo()` from a Ctrl+Z / Cmd+Z keydown handler and `redo()` from a
 * Ctrl+Shift+Z / Ctrl+Y handler.
 */
export function useUndoStack({
  query,
  focusOverride,
  state,
  dispatch,
}: {
  query: string;
  focusOverride: FocusOverride | null;
  state: GridSelectionState | null;
  dispatch: Dispatch<QueryBuilderAction>;
}): { undo: () => void; redo: () => void } {
  const undoStackRef = useRef<UndoItem[]>([]);
  const redoStackRef = useRef<UndoItem[]>([]);
  // True while an undo/redo-driven query change is propagating, so the snapshot
  // effect below doesn't mistake the restored query for a new edit (which would
  // discard the redo history).
  const isUndoRedoRef = useRef(false);

  // Snapshot the current state into the undo stack. Track focusedKey via a primitive
  // dep so the effect doesn't re-run on every selection state object reallocation.
  const focusedKey = state?.focusedKey ?? null;
  useEffect(() => {
    const wasUndoRedo = isUndoRedoRef.current;
    isUndoRedoRef.current = false;
    const pushed = updateUndoStack({
      undoStack: undoStackRef.current,
      query,
      focusOverride,
      focusedKey,
    });
    // A genuinely new edit invalidates any redo history; a change driven by
    // undo/redo does not.
    if (pushed && !wasUndoRedo) {
      redoStackRef.current = [];
    }
  }, [query, focusOverride, focusedKey]);

  const undo = useCallback(() => {
    const stack = undoStackRef.current;
    if (stack.length === 0) {
      return;
    }
    // Walk back past all entries that match the current query.
    let i = stack.length - 1;
    while (i > 0 && stack[i].query === query) {
      i--;
    }
    const previousItem = stack[i];
    if (previousItem.query !== query) {
      // Move the undone entries (everything above the target) onto the redo stack,
      // then truncate them so the subsequent re-render sees the target as the
      // current top and doesn't push a duplicate (the undo/redo oscillation bug).
      for (const item of stack.slice(i + 1)) {
        redoStackRef.current.push(item);
      }
      stack.splice(i + 1);
      isUndoRedoRef.current = true;
      dispatch({ type: 'UPDATE_QUERY', query: previousItem.query, focusOverride: focusOverrideForItem(previousItem) });
    }
  }, [dispatch, query]);

  const redo = useCallback(() => {
    const redoStack = redoStackRef.current;
    if (redoStack.length === 0) {
      return;
    }
    const item = redoStack[redoStack.length - 1];
    redoStack.pop();
    isUndoRedoRef.current = true;
    dispatch({ type: 'UPDATE_QUERY', query: item.query, focusOverride: focusOverrideForItem(item) });
  }, [dispatch]);

  return { undo, redo };
}
